import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { DocumentPaper } from '@goyone/shared';
import { api } from '../../lib/api';
import { DocumentView, type DocData, type DocProfile } from '../print/PrintDocument';
import '../../styles/print.css';

/** ما يعيده `/public/invoices/:token` — ملفُّ الشركة على المستند، والورقة، والفاتورة. */
interface PublicInvoicePage extends DocProfile {
  paper: DocumentPaper | null;
  invoice: DocData;
}

/**
 * الفاتورةُ كما يفتحها العميل من رابطها — من كشف حسابه أو رسالةٍ أُرسلت إليه.
 *
 * المستندُ نفسه الذي يطبعه الفريق (DocumentView): ترويسةُ الشركة أو ورقتُها،
 * والبنودُ والإجماليّات، ومعها الدفعاتُ المحصّلة والمتبقّي — فتكتمل مراجعتُه
 * من غير أن يسأل. بلا شريطٍ ولا قائمةٍ ولا دخول: الرمزُ في الرابط هو الإذن،
 * والشركةُ من النطاق. وزرُّ الطباعة يحفظها PDF كما تُطبع.
 */
export function PublicInvoice() {
  const { token = '' } = useParams();

  const page = useQuery({
    queryKey: ['public-invoice', token],
    queryFn: () => api<PublicInvoicePage>(`/public/invoices/${encodeURIComponent(token)}`),
    retry: false,
    staleTime: Infinity,
  });

  /* عنوانُ التبويب رقمُ الفاتورة — ويُحفظ به ملفُّ PDF افتراضيًّا. */
  useEffect(() => {
    if (page.data) document.title = `فاتورة ${page.data.invoice.number} — ${page.data.company.legalName || page.data.tenant.name}`;
  }, [page.data]);

  if (page.isPending) {
    return (
      <div className="print-page">
        <p>جارٍ تحميل الفاتورة…</p>
      </div>
    );
  }

  if (page.isError) {
    return (
      <div className="print-page">
        <h1 style={{ fontSize: '14pt' }}>الرابط لا يعمل</h1>
        <p>
          {page.error instanceof Error
            ? page.error.message
            : 'هذه الفاتورة غير موجودة أو لم يعد رابطها متاحًا.'}
        </p>
        <p className="small mute">تواصل مع الشركة التي أرسلت إليك الرابط.</p>
      </div>
    );
  }

  const { paper, invoice, ...profile } = page.data;
  return (
    <DocumentView
      kind="invoice"
      d={invoice}
      profile={profile}
      paper={paper}
      closable={false}
      showPayments
      toolbarNote="لحفظ الفاتورة ملفًّا اختر «حفظ كـ PDF» في نافذة الطباعة."
    />
  );
}
